import React from "react";
import { useNotification, useTranslate } from "@refinedev/core";
import { ConfigProvider, Flex } from "antd";
import { ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { Button } from "@libertify/ui";
import styled, { ThemeProvider } from "styled-components";
import { useAntTheme } from "hooks/useAntTheme";
import { useOnboardingQuestionnaire } from "../creator-profile/hooks/useOnboardingQuestionnaire";
import {
  TeacherOnboardingProvider,
  useTeacherOnboarding,
} from "./teacherOnboardingContext";
import WelcomeStep from "./WelcomeStep";
import ChoiceStep from "./ChoiceStep";
import SliderStep from "./SliderStep";
import GuardrailStep from "./GuardrailStep";
import FinishStep from "./FinishStep";
import PhaseStepper from "./PhaseStepper";
import { BODY_FONT, I18N_PREFIX } from "./constants";
import { buildTeacherQuestionnaire } from "./submission";
import { TEACHER_STEPS } from "./types";
import type { TeacherAnswers, TeacherStep } from "./types";

// Question steps = everything between welcome and finish.
const QUESTION_COUNT = TEACHER_STEPS.length - 2;

const STEP_COMPONENTS: Record<TeacherStep, React.FC> = {
  welcome: WelcomeStep,
  voice1: ChoiceStep,
  voice2: ChoiceStep,
  illustration: ChoiceStep,
  structure: ChoiceStep,
  depth: SliderStep,
  autonomy: SliderStep,
  guardrail: GuardrailStep,
  finish: FinishStep,
};

// Choice steps whose answer is required before Continue.
const REQUIRED_CHOICE: Partial<Record<TeacherStep, keyof TeacherAnswers>> = {
  voice1: "voice1",
  voice2: "voice2",
  illustration: "illustration",
  structure: "structure",
};

const Wizard = () => {
  const t = useTranslate();
  const { open } = useNotification();
  const {
    template,
    submit: submitQuestionnaire,
    isSubmitting,
  } = useOnboardingQuestionnaire("teacher");
  const {
    currentStep,
    answers,
    goForward,
    goBack,
    commitStaged,
    isFirstStep,
    stepNumber,
  } = useTeacherOnboarding();

  const StepComponent = STEP_COMPONENTS[currentStep];
  const isWelcome = currentStep === "welcome";
  const showNav = !isWelcome && currentStep !== "finish";
  // guardrail is the last question; continuing from it submits, then reveals finish.
  const isLastQuestion = currentStep === "guardrail";

  const requiredField = REQUIRED_CHOICE[currentStep];
  const canContinue = requiredField ? answers[requiredField] != null : true;

  const teacherTemplate = template?.type === "teacher" ? template : undefined;

  const submit = async (finalAnswers: TeacherAnswers) => {
    const questionnaire = buildTeacherQuestionnaire(
      finalAnswers,
      teacherTemplate
    );
    await submitQuestionnaire(questionnaire);
  };

  const handleContinue = async () => {
    if (!canContinue) return;
    const merged = commitStaged();
    if (isLastQuestion) {
      try {
        await submit(merged);
      } catch {
        open?.({
          type: "error",
          message: t(`${I18N_PREFIX}.errors.submitFailedTitle`),
          description: t(`${I18N_PREFIX}.errors.submitFailed`),
        });
        return;
      }
    }
    goForward();
  };

  const handleBack = () => {
    commitStaged();
    goBack();
  };

  return (
    <Flex
      vertical
      align="center"
      justify="center"
      gap={isWelcome ? 24 : 40}
      style={{
        width: "100%",
        // The welcome step is two columns wide and lives in a short LTI iframe,
        // so it gets a wider box and tighter padding than the question steps.
        maxWidth: isWelcome ? 980 : 780,
        // 100vh = the LTI iframe's height (this step renders with no app
        // layout around it), so the step sits centred in whatever Moodle gives us.
        minHeight: isWelcome ? "100vh" : undefined,
        margin: "0 auto",
        padding: isWelcome ? "24px 24px 32px" : "40px 20px 80px",
        fontFamily: BODY_FONT,
      }}
    >
      {!isWelcome && <PhaseStepper />}

      <StepComponent key={currentStep} />

      {showNav && (
        <Flex align="center" gap={12} style={{ width: "100%", maxWidth: 560 }}>
          {!isFirstStep && (
            <Button variant="outlined" size="l" onClick={handleBack}>
              <ArrowLeftIcon />
              {t(`${I18N_PREFIX}.nav.back`)}
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <StepCount>
            {stepNumber} / {QUESTION_COUNT}
          </StepCount>
          <Button
            variant="primary"
            size="l"
            disabled={!canContinue || isSubmitting}
            onClick={handleContinue}
          >
            {t(`${I18N_PREFIX}.nav.continue`)}
            <ArrowRightIcon />
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

const TEAL_SEED = "#1FB5AC";

const StyledThemeBridge = ({ children }: { children: React.ReactNode }) => {
  const { theme } = useAntTheme();
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export type TeacherOnboardingPageProps = {
  /** Called from the finish step's CTA; defaults to navigating to the projects list. */
  onComplete?: () => void;
};

const TeacherOnboardingPage = ({ onComplete }: TeacherOnboardingPageProps) => (
  <ConfigProvider
    theme={{ token: { colorPrimary: TEAL_SEED, fontFamily: BODY_FONT } }}
  >
    <StyledThemeBridge>
      <TeacherOnboardingProvider onComplete={onComplete}>
        <Wizard />
      </TeacherOnboardingProvider>
    </StyledThemeBridge>
  </ConfigProvider>
);

export default TeacherOnboardingPage;

// ─── Styled components ───

const StepCount = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colorTextSecondary};
`;
